import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useAuth } from './auth/AuthContext';
import { Splash } from './components/common';
import { homePath, Layout } from './components/Layout';
import { DashboardPage } from './pages/DashboardPage';
import { LoginPage } from './pages/LoginPage';
import { NewEntryPage } from './pages/NewEntryPage';
import { ForcePasswordPage } from './pages/ProfilePage';
import { PropertiesPage } from './pages/PropertiesPage';
import { PropertyDetailsPage } from './pages/PropertyDetailsPage';
import { RequestDetailsPage } from './pages/RequestDetailsPage';
import { RequestsPage } from './pages/RequestsPage';
import { NotificationsPage } from './pages/NotificationsPage';
import { RolesPage } from './pages/RolesPage';
import { UsersPage } from './pages/UsersPage';
import { SettingsPage } from './components/SettingsDialog';
import type { Permission } from '@re/shared';
import { lazy, Suspense, type ReactElement } from 'react';

// the map page carries the map library: loaded only when opened
const MapPage = lazy(() => import('./pages/MapPage').then((m) => ({ default: m.MapPage })));


function Guard({ perms, children }: { perms: Permission[]; children: ReactElement }) {
  const { can } = useAuth();
  return can(...perms) ? children : <Navigate to="/" replace />;
}

export function App() {
  const { status, me, can } = useAuth();
  const loc = useLocation();
  const home = homePath(can);
  if (status === 'loading') return <Splash />;
  if (status === 'anonymous') {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="*" element={<Navigate to="/login" replace state={{ from: loc.pathname + loc.search }} />} />
      </Routes>
    );
  }
  // spec: accounts created by an admin must set their own password first
  if (me?.user.must_change_password) return <ForcePasswordPage />;

  return (
    <Routes>
      <Route element={<Layout />}>
        <Route index element={home ? <Navigate to={home} replace /> : null} />
        <Route path="/dashboard" element={<Guard perms={['dashboard.view']}><DashboardPage /></Guard>} />
        <Route path="/new" element={<Guard perms={['form.submit']}>{can('properties.create', 'requests.create') ? <NewEntryPage /> : <Navigate to="/" replace />}</Guard>} />
        <Route path="/properties" element={<Guard perms={['properties.view.all', 'properties.view.own']}><PropertiesPage /></Guard>} />
        <Route path="/properties/:id" element={<Guard perms={['properties.view.all', 'properties.view.own']}><PropertyDetailsPage /></Guard>} />
        <Route path="/map" element={<Guard perms={['properties.view.all', 'properties.view.own']}><Suspense fallback={<Splash />}><MapPage /></Suspense></Guard>} />
        <Route path="/requests" element={<Guard perms={['requests.view.all', 'requests.view.own']}><RequestsPage /></Guard>} />
        <Route path="/requests/:id" element={<Guard perms={['requests.view.all', 'requests.view.own']}><RequestDetailsPage /></Guard>} />
        <Route path="/users" element={<Guard perms={['users.view', 'users.manage']}><UsersPage /></Guard>} />
        <Route path="/roles" element={<Guard perms={['roles.manage']}><RolesPage /></Guard>} />
        <Route path="/notifications" element={<NotificationsPage />} />
        <Route path="/settings" element={<Guard perms={['org.settings']}><SettingsPage /></Guard>} />
        <Route path="/login" element={<Navigate to="/" replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}
